import { createFileRoute } from '@tanstack/react-router'
import { Layout } from '@/components/custom/layout'
import { Button } from '@/components/ui/button'
import { ReportCustomTable } from '@/components/reports-route/customs/custom-table'
import { getFilters } from '@/api/filter'
import { useQuery } from '@tanstack/react-query'
import { CustomTablePagination } from '@/components/reports-route/customs/custom-table-pagination'
import { useAllAgent } from '@/hooks/use-setting-common'
import { formatDayWithTime } from '@/utils/date'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { deleteFilter } from '@/api/filter'
import {
  CustomModalContent,
  typeEnum,
} from '@/components/reports-route/customs/custom-modal-content'
import { useReportCustomFilterStore } from '@/store/report-custom-filter-store'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { exportTickets as exportTicketsApi } from '@/api/ticket'
import { useAllTicketExtraFields } from '@/hooks/use-setting-common'
import { createFilter } from '@/api/filter'
import { dateOptions } from '@/components/reports-route/customs/custom-modal-content'
import { updateFilter } from '@/api/filter'
import { toast } from 'sonner'
import { navigateGuard } from '@/utils/auth.js'
import { UserRole } from '@/constants/user.js'
import { Card, CardContent } from '@/components/ui/card'
import { Plus } from 'lucide-react'
import { format } from 'date-fns'
import { saveExcelFile } from '@/utils/export.js'
import { throwException } from '@/exceptions/throw-exception.js'
import QUERY_KEY from '@/constants/use-query-key.js'

export const Route = createFileRoute('/_auth/report/custom')({
  beforeLoad: ({ location }) =>
    navigateGuard(location, [
      UserRole.ADMINISTRATOR,
      UserRole.SUPERVISOR,
      UserRole.TEAM_LEADER,
    ]),
  component: CustomReport,
})

const initialFieldListData = {
  reportType: 'excel',
  timeTypes: [
    {
      timeType: null,
      timeStart: null,
      timeEnd: null,
    },
  ],
  fields: [
    {
      field: null,
    },
  ],
}

function CustomReport() {
  const extraFieldsData = useAllTicketExtraFields()
  const [refetchTime, setRefetchTime] = useState(new Date().toISOString())
  const [openModal, setOpenModal] = useState(false)
  const [typeModal, setTypeModal] = useState({ type: typeEnum.create })
  const [fieldListData, setFieldListData] = useState(initialFieldListData)
  const [reportExporting, setReportExporting] = useState(null)
  const getAllAgent = useAllAgent()

  const queryDataAllAgent = useAllAgent()
  const { page, page_size } = useReportCustomFilterStore()
  const fetchData = async () => {
    const payload = {
      type: 'report',
      check_permission: true,
      offset: (page - 1) * page_size,
      limit: page_size,
    }
    try {
      const resp = await getFilters({ params: payload })
      return resp.data
    } catch (error) {
      throwException(error)
      return {}
    }
  }

  const { isLoading, data: queryData } = useQuery({
    queryKey: [
      QUERY_KEY.GET_CUSTOM_REPORT,
      {
        params: {
          refetchTime,
          page,
          page_size,
        },
      },
    ],
    queryFn: fetchData,
  })

  const { data: dataCustom, total } = getDataCustom(
    !isLoading ? queryData : {},
    queryDataAllAgent,
  )

  const handleDelete = async (filter) => {
    await deleteFilter(filter._id)
    setRefetchTime(new Date().toISOString())
  }

  const exportTickets = async (data) => {
    setReportExporting(data._id)
    const { params, body } = await handleExportTicket(data, getAllAgent)
    const resp = await exportTicketsApi({ params, data: body })
    return resp
  }
  const { isPending: isExporting, mutate: onExportClick } = useMutation({
    mutationFn: exportTickets,
    onSuccess: (data) => {
      const date = new Date()
      saveExcelFile(
        data,
        `Danh sách ticket ${format(date, 'dd-MM-yyyy HH-mm')}`,
      )
    },
    onError: throwException,
  })

  const { isPending: isLoadingCreate, mutateAsync: CreateCampaignMutation } =
    useMutation({
      mutationFn: createFilter,
      onSuccess: () => {
        toast.success('Tạo báo cáo thành công!')
        setOpenModal(false)
        setRefetchTime(new Date().toISOString())
      },
      onError: throwException,
    })

  const { isPending: isLoadingUpdate, mutateAsync: UpdateCampaignMutation } =
    useMutation({
      mutationFn: (data) => {
        updateFilter(data.filterId, data.payload)
      },
      onSuccess: () => {
        setOpenModal(false)
        setRefetchTime(new Date().toISOString())
        toast.success('Cập nhật báo cáo thành công!')
      },
      onError: throwException,
    })

  const handleSubmit = async () => {
    const payload = buildBody(fieldListData, queryDataAllAgent, extraFieldsData)
    payload.name = payload.name.trim()
    if (!payload.name) {
      return toast.error('Tên báo cáo không được để trống')
    }
    if (typeModal.type === typeEnum.create) {
      try {
        await CreateCampaignMutation(payload)
      } catch (error) {
        throwException(error)
      }
    }

    if (typeModal.type === typeEnum.edit) {
      try {
        await UpdateCampaignMutation({ filterId: typeModal.filterId, payload })
      } catch (error) {
        throwException(error)
      }
    }
  }

  const showDetailFilter = async (filter) => {
    const payload = parseBody(
      filter,
      queryDataAllAgent,
      extraFieldsData,
      dateOptions,
    )
    payload.reportType = 'excel'
    setFieldListData(payload)
    setTypeModal({ type: typeEnum.edit, filterId: filter._id })
    setOpenModal(true)
  }

  const handleCreateOpen = () => {
    setFieldListData({
      reportType: 'excel',
      timeTypes: [
        {
          timeType: null,
          timeStart: null,
          timeEnd: null,
        },
      ],
      fields: [
        {
          field: null,
        },
      ],
    })
    setTypeModal({ type: typeEnum.create })
    setOpenModal(true)
  }
  const loadingSubmit = isLoadingCreate || isLoadingUpdate

  return (
    <Layout.Body>
      <div>
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Danh sách báo cáo</h1>
          <Dialog open={openModal} onOpenChange={setOpenModal}>
            <DialogTrigger asChild>
              <Button onClick={handleCreateOpen}>
                <Plus /> Thêm báo cáo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[80%] overflow-y-auto sm:max-w-[1000px]">
              <CustomModalContent
                loadingSubmit={loadingSubmit}
                type={typeModal.type}
                handleSubmit={handleSubmit}
                fieldListData={fieldListData}
                setFieldListData={setFieldListData}
                getAllAgent={getAllAgent}
              />
            </DialogContent>
          </Dialog>
        </div>
        <Card className="mx-auto w-full max-w-full">
          <CardContent className="!p-6">
            <ReportCustomTable
              reportExporting={reportExporting}
              showDetailFilter={showDetailFilter}
              onExportClick={onExportClick}
              isExporting={isExporting}
              pageSize={page_size}
              isLoading={false}
              data={dataCustom}
              handleDelete={handleDelete}
            />
            <CustomTablePagination totalRecords={total} />
          </CardContent>
        </Card>
      </div>
    </Layout.Body>
  )
}

const getDataCustom = (data, queryDataAllAgent) => {
  if (!data || !data.data || data.data.length <= 0) {
    return {
      data: [],
      total: 0,
    }
  }

  const dataFormat = data.data.map((item, index) => {
    return {
      ...item,
      STT: index + 1,
      types: 'Dạng excel',
      created_at: formatDayWithTime(item.created_at),
      agent: queryDataAllAgent.find((agent) => agent._id === item.user_id)
        ?.name,
    }
  })

  return { data: dataFormat, total: data.total }
}

const buildBody = (data, queryDataAllAgent, extraFieldsData) => {
  let body = {
    data: {},
    group_id: data.group_id && Number(data.group_id),
    is_assign_agent_id: true,
    main_and_or: 'and',
    name: data.name,
    organize_id: data.organize_id,
    sub_and_or: 'and',
    type: 'report',
    type_filter: 'customize_report',
  }

  const condition = {}

  if (data.status && data.status !== '-1') {
    condition['status'] = [{ label: 'eq', val: data.status }]
  }

  if (data.customerGroup_id && data.customerGroup_id !== '-1') {
    condition['customerGroup_id'] = data.customerGroup_id
  }

  if (data.source_detail && data.source_detail !== '-1') {
    condition['source_detail'] = [{ label: 'eq', val: data.source_detail }]
  }

  if (
    (data.organize_id && data.organize_id !== '-1') ||
    (data.organize_id !== '-1' && data.organize_id)
  ) {
    condition['assign_agent_id'] = queryDataAllAgent
      .filter((e) => {
        if (data.organize_id && data.organize_id !== '-1') {
          return e.organize_id == data.organize_id
        }

        if (data.group_id && data.group_id !== '-1') {
          return e.group_id == data.group_id
        }

        return e.organize_id == data.organize_id && e.group_id == data.group_id
      })
      .map((e) => ({
        label: 'eq',
        val: e._id,
      }))
  }

  if (data.user_id && data.user_id !== '-1') {
    condition['assign_agent_id'] = [{ label: 'eq', val: data.user_id }]
  }

  if (data.timeTypes && data.timeTypes.length > 0) {
    data.timeTypes.map((e) => {
      condition[e.timeType] = []
      if (e.timeStart) {
        condition[e.timeType].push({ label: 'gte', val: e.timeStart })
      }
      if (e.timeEnd) {
        condition[e.timeType].push({ label: 'lte', val: e.timeEnd })
      }
    })
  }

  if (data.fields && data.fields.length > 0) {
    data.fields.map((e) => {
      if (e.field) {
        const extraField = extraFieldsData.find((item) => item.id === e.field)
        if (extraField.type === 'multi-select') {
          condition[e.field] = []
          e.fieldChild?.map((item) => {
            condition[e.field].push({ label: 'eq', val: item })
          })
        }

        if (extraField.type === 'date') {
          condition[e.field] = []
          if (e.timeStart) {
            condition[e.field].push({ label: 'gte', val: e.timeStart })
          }
          if (e.timeEnd) {
            condition[e.field].push({ label: 'lte', val: e.timeEnd })
          }
        }

        if (extraField.type !== 'date' && extraField.type !== 'multi-select') {
          condition[e.field] = [{ label: 'eq', val: e.fieldChild }]
        }
      }
    })
  }

  const conditionJson = JSON.stringify(condition)

  body.data = conditionJson
  return body
}

const parseBody = (body, queryDataAllAgent, extraFieldsData, dateOptions) => {
  let data = {
    group_id: body.group_id?.toString(),
    name: body.name,
    organize_id: body.organize_id,
    is_assign_agent_id: body.is_assign_agent_id,
    main_and_or: body.main_and_or,
    sub_and_or: body.sub_and_or,
    type: body.type,
    type_filter: body.type_filter,
  }

  const condition = JSON.parse(body.data)

  // Parse status
  if (condition.status && condition.status[0].val) {
    data.status = condition.status[0].val
  }

  // Parse customerGroup_id
  if (condition.customerGroup_id) {
    data.customerGroup_id = condition.customerGroup_id
  }

  // Parse source_detail
  if (condition.source_detail && condition.source_detail[0].val) {
    data.source_detail = condition.source_detail[0].val
  }

  // Parse assign_agent_id
  if (condition.assign_agent_id && condition.assign_agent_id.length == 1) {
    const agents = queryDataAllAgent.filter((e) =>
      condition.assign_agent_id.find((item) => item.val === e.agent_id),
    )
    if (agents.length > 0) {
      if (agents.length === 1) {
        data.user_id = agents[0].agent_id
      }
    }
  }

  // Parse timeTypes
  data.timeTypes = []
  Object.keys(condition).forEach((key) => {
    if (dateOptions.find((item) => item.id === key)) {
      const timeType = {
        timeType: key,
      }
      condition[key].forEach((cond) => {
        if (cond.label === 'gte') {
          timeType.timeStart = cond.val
        } else if (cond.label === 'lte') {
          timeType.timeEnd = cond.val
        }
      })
      data.timeTypes.push(timeType)
    }
  })

  // Parse fields
  data.fields = []
  Object.keys(condition).forEach((key) => {
    const extraField = extraFieldsData.find((item) => item.id === key)
    if (extraField) {
      const field = { field: key }
      if (extraField.type === 'multi-select') {
        field.fieldChild = condition[key].map((item) => item.val)
      } else if (extraField.type === 'date') {
        condition[key].forEach((cond) => {
          if (cond.label === 'gte') {
            field.timeStart = cond.val
          } else if (cond.label === 'lte') {
            field.timeEnd = cond.val
          }
        })
      } else {
        field.fieldChild = condition[key][0].val
      }
      data.fields.push(field)
    }
  })

  if (data.timeTypes.length === 0) {
    data.timeTypes = [{ timeType: null }]
  }

  if (data.fields.length === 0) {
    data.fields = [{ field: null }]
  }

  return data
}

const handleExportTicket = (item, getAllAgent) => {
  let data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data
  if (item.group_id || item.organize_id) {
    data.assign_agent_id = getAllAgent
      .filter(
        (e) =>
          (item.group_id &&
            item.organize_id &&
            item.group_id === e.group_id &&
            item.organize_id === e.organize_id) ||
          (item.group_id && item.group_id === e.group_id) ||
          (item.organize_id && item.organize_id === e.organize_id),
      )
      .map((a) => ({
        label: 'eq',
        val: a._id,
      }))
  }

  const params = {
    source: 'all',
  }
  let condition = { must: [] }

  Object.keys(data)
    .filter((key) => key !== 'customerGroup_id')
    .forEach((key) => {
      let object = {
        bool: {},
      }
      switch (key) {
        case 'assign_agent_id':
          object.bool.should = data[key].map((e) => ({
            match_phrase: { [key]: e.val },
          }))
          break
        case 'time_start':
        case 'time_end':
        case 'created_at':
        case 'updated_at':
        case 'thoidiem':
          object.bool.must = data[key].map((e) => ({
            range: {
              [key]: {
                [e.label]: e.val,
              },
            },
          }))
          break

        default:
          object.bool.must = data[key].map((e) => ({
            match_phrase: { [key]: e.val },
          }))

          break
      }

      condition.must.push(object)
    })

  let dataCustomerByGroup = []

  if ('customerGroup_id' in data && data.customerGroup_id) {
    let payload = {
      active_fields: JSON.stringify(['group_id']),
      force_search: 'customer',
      label: 'and',
      sub_label: 'and',
      type: 'user',
      condition: {
        must: [
          {
            bool: {
              must: [
                {
                  match_phrase: {
                    group_id: data.customerGroup_id.toString(),
                  },
                },
              ],
            },
          },
        ],
      },
    }

    payload.condition = JSON.stringify(payload.condition)
    this.searchService.all(payload).subscribe((res) => {
      dataCustomerByGroup = res.data.list

      if (dataCustomerByGroup.length > 0) {
        let object = {
          bool: {
            should: [],
          },
        }
        dataCustomerByGroup.map((e) => {
          object.bool.should.push({
            match_phrase: {
              requester_id: e._id,
            },
          })
        })
        condition.must.push(object)
      }

      const body = {
        condition: JSON.stringify(condition),
        active_fields: JSON.stringify(['assign_agent_id']),
        type: 'ticket',
        label: 'and',
        sub_label: 'and',
        keyword: '',
        force_search: 'customer',
      }

      return { params, body }
    })
  } else {
    const body = {
      condition: JSON.stringify(condition),
      active_fields: JSON.stringify(['assign_agent_id']),
      type: 'ticket',
      label: 'and',
      sub_label: 'and',
      keyword: '',
      force_search: 'customer',
    }
    return { params, body }
  }
}
